import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Clock, Calendar, DollarSign, CheckCircle, Play, Square, MapPin, Award, 
  FileText, Download, TrendingUp, Star
} from 'lucide-react';

const REFEREE_BG = "https://images.unsplash.com/photo-1757031298879-08c2aba66330?crop=entropy&cs=srgb&fm=jpg&q=85";

export const RefereePortalPage = () => {
  const { api, user } = useAuth();
  const [games, setGames] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [refereeProfile, setRefereeProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockedIn, setClockedIn] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gamesRes, earningsRes, refereesRes] = await Promise.all([
          api().get('/games'),
          api().get('/referee/earnings').catch(() => ({ data: null })),
          api().get('/referees')
        ]);
        setGames(gamesRes.data);
        setEarnings(earningsRes.data);
        // Find referee profile linked to current user
        const myProfile = refereesRes.data.find(r => r.user_id === user?.id);
        setRefereeProfile(myProfile);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, user]);

  const handleClockIn = async (gameId) => {
    try {
      await api().post(`/clock/in?game_id=${gameId}`);
      setClockedIn({ ...clockedIn, [gameId]: true });
      toast.success('Clocked in successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async (gameId) => {
    try {
      await api().post(`/clock/out?game_id=${gameId}`);
      setClockedIn({ ...clockedIn, [gameId]: false });
      toast.success('Clocked out successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  const downloadTaxReport = (year) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/referee/tax-report/${year}/pdf`, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
    });
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A': 'text-emerald-400 bg-emerald-900/20 border-emerald-900',
      'B': 'text-blue-400 bg-blue-900/20 border-blue-900',
      'C': 'text-yellow-400 bg-yellow-900/20 border-yellow-900',
      'D': 'text-orange-400 bg-orange-900/20 border-orange-900',
      'F': 'text-red-400 bg-red-900/20 border-red-900'
    };
    return colors[grade] || colors['B'];
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-900/30 text-blue-400 border-blue-900',
      in_progress: 'bg-yellow-900/30 text-yellow-400 border-yellow-900',
      completed: 'bg-green-900/30 text-green-400 border-green-900'
    };
    return colors[status] || colors.scheduled;
  };

  const upcomingGames = games.filter(g => g.status === 'scheduled' || g.status === 'in_progress');
  const pastGames = games.filter(g => g.status === 'completed');
  const currentYear = new Date().getFullYear();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="referee-portal" className="space-y-8 animate-fade-in">
      {/* Hero Header */}
      <div 
        className="relative h-48 bg-cover bg-center rounded-none overflow-hidden"
        style={{ backgroundImage: `url(${REFEREE_BG})` }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-center p-8">
          <h1 className="text-4xl font-heading font-bold uppercase tracking-wide">Referee Portal</h1>
          <p className="text-zinc-300 mt-2">Welcome back, {user?.name}</p>
        </div>
      </div>

      {/* Grade & Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Grade Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Your Grade</p>
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-sm flex items-center justify-center text-3xl font-heading font-bold border ${getGradeColor(earnings?.grade || refereeProfile?.grade || 'B')}`}>
              {earnings?.grade || refereeProfile?.grade || 'B'}
            </div>
            <div>
              <p className="text-sm text-zinc-400">Score: {(earnings?.grade_score || refereeProfile?.grade_score || 80).toFixed(1)}%</p>
              <div className="flex items-center gap-1 mt-1">
                {[1,2,3,4,5].map((star) => (
                  <Star 
                    key={star} 
                    className={`w-3 h-3 ${star <= Math.round((earnings?.grade_score || 80) / 20) ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-700'}`} 
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Total Earnings</p>
              <p className="text-3xl font-heading font-bold text-emerald-400">{formatCurrency(earnings?.total_earnings)}</p>
            </div>
            <div className="p-3 bg-emerald-900/20 rounded-sm">
              <DollarSign className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Games Completed</p>
              <p className="text-3xl font-heading font-bold text-blue-400">{earnings?.completed_games || pastGames.length}</p>
            </div>
            <div className="p-3 bg-blue-900/20 rounded-sm">
              <CheckCircle className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Hourly Rate</p>
              <p className="text-3xl font-heading font-bold text-purple-400">{formatCurrency(earnings?.hourly_rate || refereeProfile?.hourly_rate)}/hr</p>
            </div>
            <div className="p-3 bg-purple-900/20 rounded-sm">
              <Award className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Tax Reports */}
      <div className="bg-zinc-900/50 border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-bold uppercase tracking-wide">Tax Reports</h2>
          <FileText className="w-5 h-5 text-zinc-500" />
        </div>
        <p className="text-sm text-zinc-400 mb-4">Download your annual earnings report for tax purposes.</p>
        <div className="flex gap-2">
          <Button
            onClick={() => downloadTaxReport(currentYear)}
            data-testid="download-tax-report-current"
            variant="outline"
            className="border-zinc-700"
          >
            <Download className="w-4 h-4 mr-2" />
            {currentYear} Report
          </Button>
          <Button
            onClick={() => downloadTaxReport(currentYear - 1)}
            data-testid="download-tax-report-previous"
            variant="outline"
            className="border-zinc-700"
          >
            <Download className="w-4 h-4 mr-2" />
            {currentYear - 1} Report
          </Button>
        </div>
      </div>

      {/* Upcoming Games */}
      <div>
        <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4">Upcoming Assignments</h2>
        {upcomingGames.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-zinc-900/50 border border-zinc-800">
            <Calendar className="w-10 h-10 text-zinc-600 mb-3" />
            <p className="text-zinc-500">No upcoming assignments</p>
          </div>
        ) : (
          <div className="space-y-4">
            {upcomingGames.map((game) => (
              <div 
                key={game.id}
                data-testid={`game-assignment-${game.id}`}
                className="bg-zinc-900/50 border border-zinc-800 p-5 hover:border-zinc-700 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading font-bold text-lg">{game.tournament_event}</h3>
                      <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(game.status)}`}>
                        {game.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> {formatDate(game.game_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {game.location}
                      </span>
                      <span>{game.division}</span>
                      {game.court_number && (
                        <span className="text-blue-400 font-medium">Court: {game.court_number}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-emerald-400 font-medium">{formatCurrency(game.rate * game.num_games)}</p>
                      <p className="text-xs text-zinc-500">{game.num_games} game(s) @ ${game.rate}/game</p>
                    </div>
                    
                    {game.status === 'scheduled' && (
                      <Button
                        onClick={() => clockedIn[game.id] ? handleClockOut(game.id) : handleClockIn(game.id)}
                        data-testid={`clock-btn-${game.id}`}
                        className={clockedIn[game.id] 
                          ? "bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded-sm"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide rounded-sm"
                        }
                      >
                        {clockedIn[game.id] ? (
                          <><Square className="w-4 h-4 mr-2" /> Clock Out</>
                        ) : (
                          <><Play className="w-4 h-4 mr-2" /> Clock In</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Games */}
      <div>
        <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4">Completed Games</h2>
        {pastGames.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-zinc-900/50 border border-zinc-800">
            <CheckCircle className="w-10 h-10 text-zinc-600 mb-3" />
            <p className="text-zinc-500">No completed games yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pastGames.slice(0, 10).map((game) => (
              <div 
                key={game.id}
                className="bg-zinc-900/50 border border-zinc-800 p-4 flex items-center justify-between"
              >
                <div>
                  <h3 className="font-medium">{game.tournament_event}</h3>
                  <p className="text-sm text-zinc-500">{formatDate(game.game_date)} • {game.location}</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 font-medium">{formatCurrency(game.rate * game.num_games)}</p>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-900/30 text-green-400 border border-green-900">
                    Completed
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const ClientPortalPage = () => {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api().get('/invoices');
        setInvoices(response.data);
      } catch (error) {
        console.error('Failed to fetch invoices:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [api, searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus({ status: 'timeout', message: 'Payment status check timed out' });
      return;
    }

    try {
      const response = await api().get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus({ status: 'success', message: 'Payment successful!' });
        const invoicesRes = await api().get('/invoices');
        setInvoices(invoicesRes.data);
      } else if (response.data.status === 'expired') {
        setPaymentStatus({ status: 'error', message: 'Payment session expired' });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setPaymentStatus({ status: 'error', message: 'Error checking payment status' });
    }
  };

  const initiatePayment = async (invoiceId) => {
    try {
      const response = await api().post('/payments/checkout', {
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const downloadPDF = (invoiceId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}/pdf`, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-zinc-800 text-zinc-300 border-zinc-700',
      sent: 'bg-blue-900/30 text-blue-400 border-blue-900',
      paid: 'bg-green-900/30 text-green-400 border-green-900',
      overdue: 'bg-red-900/30 text-red-400 border-red-900'
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const totalOutstanding = invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.total_due, 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total_due, 0);

  return (
    <div data-testid="client-portal" className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Client Portal</h1>
        <p className="text-muted-foreground mt-1">Welcome, {user?.name}</p>
      </div>

      {paymentStatus && (
        <div className={`p-4 rounded-sm flex items-center gap-3 ${
          paymentStatus.status === 'success' 
            ? 'bg-green-900/20 border border-green-900 text-green-400'
            : paymentStatus.status === 'error'
            ? 'bg-red-900/20 border border-red-900 text-red-400'
            : 'bg-yellow-900/20 border border-yellow-900 text-yellow-400'
        }`}>
          <CheckCircle className="w-5 h-5" />
          {paymentStatus.message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Outstanding Balance</p>
          <p className="text-3xl font-heading font-bold text-yellow-400">{formatCurrency(totalOutstanding)}</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Total Paid</p>
          <p className="text-3xl font-heading font-bold text-emerald-400">{formatCurrency(totalPaid)}</p>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4">Your Invoices</h2>
        {invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-zinc-900/50 border border-zinc-800">
            <FileText className="w-10 h-10 text-zinc-600 mb-3" />
            <p className="text-zinc-500">No invoices yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <div 
                key={invoice.id}
                data-testid={`client-invoice-${invoice.id}`}
                className="bg-zinc-900/50 border border-zinc-800 p-5 hover:border-zinc-700 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-mono text-lg">{invoice.invoice_number}</h3>
                      <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </div>
                    <p className="text-zinc-400">{invoice.tournament_event} • {invoice.location}</p>
                    <p className="text-sm text-zinc-500">Due: {formatDate(invoice.due_date)}</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-2xl font-heading font-bold text-emerald-400">{formatCurrency(invoice.total_due)}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadPDF(invoice.id)}
                        data-testid={`client-download-pdf-${invoice.id}`}
                        className="border-zinc-700"
                      >
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                      {invoice.status !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => initiatePayment(invoice.id)}
                          data-testid={`client-pay-btn-${invoice.id}`}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Pay Now
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
